import { useContext, useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Spin } from 'antd';
import toast from 'react-hot-toast';
import Button from 'app/atoms/Button';
import ConfirmationModal from 'app/atoms/ConfirmationModal';
import { NavBreadcrumbContext } from 'app/molecules/NavBreadcrumb/NavBreadcrumbContext';
import { matchesSearch, matchesFilters } from 'utils/filter';
import { getUsersList, deactivateUser } from './service';
import { User } from './types';
import UserTable from './UserTable';
import InviteUserModal from './InviteUserModal';
import EmptyState from './EmptyState';

const UserManagement = () => {
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [userToDeactivate, setUserToDeactivate] = useState<User | null>(null);
  const queryClient = useQueryClient();
  const { setItems } = useContext(NavBreadcrumbContext);

  useEffect(() => {
    setItems([{ title: 'Dashboard', path: '/' }, { title: 'User Management' }]);
  }, [setItems]);

  const { data: users = [], isLoading } = useQuery({
    queryKey: ['users-list'],
    queryFn: getUsersList,
  });

  const deactivateMutation = useMutation({
    mutationFn: deactivateUser,
    onSuccess: () => {
      toast.success('User deactivated');
      queryClient.invalidateQueries({ queryKey: ['users-list'] });
      setUserToDeactivate(null);
    },
    onError: () => toast.error('Failed to deactivate user'),
  });

  const filteredUsers = useMemo(
    () =>
      users.filter(
        (user) =>
          matchesSearch(user, ['displayName'], filters.displayName ?? '') &&
          matchesSearch(user, ['email'], filters.email ?? '') &&
          matchesFilters(user, { role: filters.role, status: filters.status }),
      ),
    [users, filters],
  );

  const handleFilter = (dataIndex: string, value: string) => {
    setFilters((prev) => ({ ...prev, [dataIndex]: value }));
  };

  return (
    <div className="mx-auto max-2xl:max-w-[calc(100vw-180px)] max-sm:max-w-[calc(100vw-120px)] max-sm:px-0">
      <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h2 className="text-xl font-semibold font-figtree">User Management</h2>
        <Button variant="primary" onClick={() => setIsInviteModalOpen(true)}>
          Invite new user
        </Button>
      </div>

      <Spin spinning={isLoading}>
        {!isLoading && users.length === 0 ? (
          <EmptyState onInvite={() => setIsInviteModalOpen(true)} />
        ) : (
          <UserTable users={filteredUsers} filters={filters} onFilter={handleFilter} onDeactivate={setUserToDeactivate} />
        )}
      </Spin>

      <InviteUserModal
        visible={isInviteModalOpen}
        onCancel={() => setIsInviteModalOpen(false)}
        onInvited={() => {
          setIsInviteModalOpen(false);
          queryClient.invalidateQueries({ queryKey: ['users-list'] });
        }}
      />

      <ConfirmationModal
        isVisible={!!userToDeactivate}
        title="Deactivate user?"
        description={`${userToDeactivate?.displayName || userToDeactivate?.email} will no longer be able to sign in. Their content is left untouched.`}
        onCancel={() => setUserToDeactivate(null)}
        onConfirm={() => userToDeactivate && deactivateMutation.mutate(userToDeactivate.id)}
        isLoading={deactivateMutation.isPending}
        danger
      />
    </div>
  );
};

export default UserManagement;
